// PrimaryCTA — a single, mode-aware CTA cluster used across the homepage
// hero and high-intent service pages. The visual order swaps depending on
// the admin-controlled `cta_mode` so the owner can A/B between
// phone-first and form-first conversion paths without a rebuild.
//
// Tracking: every render fires `cta_mode_view` once per page+location, and
// each click fires its own event tagged with the active mode.

import { useEffect, useRef } from "react";
import { Phone, MessageCircle, FileText } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { getBusinessConfig } from "@/lib/site-config";
import { useCtaMode } from "@/hooks/usePublicConfig";
import { trackEvent } from "@/lib/tracking";

interface PrimaryCTAProps {
  /** Where on the page this cluster lives — used purely for analytics. */
  location: string;
  /** Optional intent label for the form CTA ("MOT", "service quote"). */
  intent?: string;
  /** Lighter theme variant for use over light backgrounds. */
  variant?: "dark" | "light";
}

export function PrimaryCTA({ location, intent = "MOT or quote", variant = "dark" }: PrimaryCTAProps) {
  const biz = getBusinessConfig();
  const mode = useCtaMode();
  const viewedFor = useRef<string>("");

  useEffect(() => {
    const key = `${mode}:${location}`;
    if (viewedFor.current === key) return;
    viewedFor.current = key;
    trackEvent("cta_mode_view", { mode, location });
  }, [mode, location]);

  const isForm = mode === "form";

  // Shared a11y polish — visible focus ring + reduced-motion friendly tap state.
  const focusRing =
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background";
  const tap = "active:scale-[0.98] motion-reduce:active:scale-100 motion-reduce:transition-none";

  // Phone button — primary in phone mode, secondary otherwise
  const phoneClasses = isForm
    ? `inline-flex items-center gap-2 rounded-lg border px-5 py-3.5 text-sm font-bold transition-all ${focusRing} ${tap} ${
        variant === "dark"
          ? "border-white/30 text-white hover:bg-white/10"
          : "border-foreground/20 text-foreground hover:bg-foreground/5"
      }`
    : `inline-flex items-center gap-2 rounded-lg bg-primary px-7 py-3.5 text-base font-bold text-primary-foreground hover:bg-primary/90 transition-all shadow-lg shadow-primary/30 ring-1 ring-primary/40 ${focusRing} ${tap}`;

  // Form button — primary in form mode, secondary otherwise
  const formClasses = isForm
    ? `inline-flex items-center gap-2 rounded-lg bg-primary px-7 py-3.5 text-base font-bold text-primary-foreground hover:bg-primary/90 transition-all shadow-lg shadow-primary/30 ring-1 ring-primary/40 ${focusRing} ${tap}`
    : `inline-flex items-center gap-2 rounded-lg border px-5 py-3.5 text-sm font-bold transition-all ${focusRing} ${tap} ${
        variant === "dark"
          ? "border-white/30 text-white hover:bg-white/10"
          : "border-foreground/20 text-foreground hover:bg-foreground/5"
      }`;

  const phoneButton = (
    <a
      key="phone"
      href={biz.phoneHref}
      onClick={() => trackEvent("cta_phone_click", { mode, location })}
      className={phoneClasses}
    >
      <Phone className={isForm ? "h-4 w-4" : "h-5 w-5"} />
      Call {biz.phone}
    </a>
  );

  const formButton = (
    <Link
      key="form"
      to="/book"
      onClick={() => trackEvent("cta_form_open", { mode, location, intent })}
      className={formClasses}
    >
      <FileText className={isForm ? "h-5 w-5" : "h-4 w-4"} />
      {isForm ? `Get a free ${intent}` : "Request a Booking"}
    </Link>
  );

  const whatsappButton = (
    <a
      key="wa"
      href={biz.whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackEvent("cta_whatsapp_click", { mode, location })}
      className={`inline-flex items-center gap-2 rounded-lg border border-[#25D366]/45 bg-[#25D366]/10 px-5 py-3.5 text-sm font-bold transition-all hover:bg-[#25D366]/20 ${focusRing} ${tap} ${
        variant === "dark" ? "text-white" : "text-foreground"
      }`}
    >
      <MessageCircle className="h-5 w-5 text-[#25D366]" />
      WhatsApp
    </a>
  );


  // Render order matters for visual hierarchy — primary CTA first.
  const buttons = isForm
    ? [formButton, phoneButton, whatsappButton]
    : [phoneButton, formButton, whatsappButton];

  return (
    <div className="mt-7 flex flex-col sm:flex-row items-start gap-2.5">
      {buttons}
    </div>
  );
}
